import { validateDiscoveryResponse } from '../03-feed-session.mjs';
// Owns reading exposure, position persistence, refresh and pull gestures.
// Injected snapshot access keeps delayed work tied to the active account.
/** Create exposure and resume handlers tied to the active account and feed generation. */
export function createReading(ctx) {
  const interestExposures = new Map();
  function finishInterestExposure(id) {
    const exposure = interestExposures.get(id);
    if (!exposure) return;
    clearInterval(exposure.timer);
    interestExposures.delete(id);
    if (
      ctx.state?.me.id !== exposure.actor ||
      !ctx.state.settings.learning ||
      (ctx.state.settings.interestEpoch ?? 0) !== exposure.epoch ||
      exposure.dwellMs < 1000
    )
      return;
    const payload = {
      eventId: crypto.randomUUID(),
      postId: id,
      snapshot: exposure.snapshot,
      format: exposure.format,
      dwellMs: Math.min(60000, Math.floor(exposure.dwellMs)),
    };
    // No offline personal-data queue. Duplicate-safe retry stays inside the same account.
    ctx.api('/api/telemetry/exposure', payload, { keepalive: true }).catch(() => {
      if (ctx.state?.me.id === exposure.actor && ctx.state.settings.learning)
        ctx.api('/api/telemetry/exposure', payload, { keepalive: true }).catch(() => {});
    });
  }
  function startInterestExposure(id, card) {
    const suggested = 'recommendationPost' in card.dataset;
    const snapshot = suggested ? card.dataset.exposureSnapshot : ctx.feed.meta?.snapshot;
    const format = suggested ? card.dataset.exposureFormat : ctx.feed.meta?.format;
    if (
      interestExposures.has(id) ||
      !ctx.state?.settings.learning ||
      !/^[a-f0-9]{48}$/.test(snapshot ?? '') ||
      !['wall', 'visual', 'talk'].includes(format)
    )
      return;
    const exposure = {
      actor: ctx.state.me.id,
      epoch: ctx.state.settings.interestEpoch ?? 0,
      snapshot,
      format,
      dwellMs: 0,
      last: performance.now(),
    };
    exposure.timer = setInterval(() => {
      if (
        document.hidden ||
        ctx.modal.open ||
        !card.isConnected ||
        ctx.state?.me.id !== exposure.actor ||
        (ctx.state.settings.interestEpoch ?? 0) !== exposure.epoch ||
        !ctx.state.settings.learning
      ) {
        finishInterestExposure(id);
        return;
      }
      const now = performance.now();
      // Cap delayed ticks: a suspended browser is not evidence of active viewing.
      exposure.dwellMs += Math.min(1500, now - exposure.last);
      exposure.last = now;
      if (exposure.dwellMs >= 60000) finishInterestExposure(id);
    }, 1000);
    interestExposures.set(id, exposure);
  }
  function clearExposure() {
    [...interestExposures.keys()].forEach(finishInterestExposure);
    ctx.exposureTimers.forEach(clearTimeout);
    ctx.exposureTimers.clear();
  }
  function saveFeedPosition() {
    if (!ctx.state || !['feed', 'learning'].includes(ctx.previousPage)) return;
    const cards = [...ctx.view.querySelectorAll('.post')];
    const anchor = cards.find((c) => c.getBoundingClientRect().bottom > 90) ?? cards.at(-1);
    if (anchor) {
      flushSeen({
        id: anchor.dataset.postId,
        offset: anchor.getBoundingClientRect().top,
      });
    } else flushSeen();
  }
  async function flushSeen(anchor) {
    clearTimeout(ctx.seenFlushTimer);
    ctx.seenFlushTimer = undefined;
    if (!ctx.state || (!ctx.pendingSeen.size && !anchor)) return;
    const actor = ctx.state.me.id;
    const ids = [...ctx.pendingSeen].slice(0, 50);
    ids.forEach((id) => ctx.pendingSeen.delete(id));
    try {
      const result = await ctx.api(
        '/api/command',
        { type: 'seen', ids, anchor },
        { keepalive: true },
      );
      if (ctx.state?.me.id === actor) ctx.state.settings.reading = result.reading;
    } catch {
      ids.forEach((id) => ctx.pendingSeen.add(id));
    }
  }
  window.addEventListener('pagehide', saveFeedPosition);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      clearExposure();
      saveFeedPosition();
    }
  });
  function observeReading() {
    ctx.seenObserver?.disconnect();
    clearExposure();
    if (!('IntersectionObserver' in window)) return;
    ctx.seenObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = entry.target.dataset.postId;
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.45 &&
            !document.hidden &&
            !ctx.modal.open
          ) {
            startInterestExposure(id, entry.target);
            // Preview exposure is a weak interest signal, never a read/resume marker.
            if ('recommendationPost' in entry.target.dataset) continue;
            if (!ctx.exposureTimers.has(id))
              ctx.exposureTimers.set(
                id,
                setTimeout(() => {
                  ctx.exposureTimers.delete(id);
                  if (!document.hidden && !ctx.modal.open && entry.target.isConnected) {
                    ctx.pendingSeen.add(id);
                    ctx.seenFlushTimer ??= setTimeout(() => {
                      ctx.seenFlushTimer = undefined;
                      flushSeen();
                    }, 1500);
                  }
                }, 1000),
              );
          } else {
            finishInterestExposure(id);
            clearTimeout(ctx.exposureTimers.get(id));
            ctx.exposureTimers.delete(id);
          }
        }
      },
      { threshold: [0, 0.45], rootMargin: '-76px 0px -65px 0px' },
    );
    ctx.view
      .querySelectorAll('#feed-list .post,[data-recommendation-post]')
      .forEach((card) => ctx.seenObserver.observe(card));
  }
  async function refreshFeed() {
    if (ctx.refreshPending) return;
    ctx.refreshPending = true;
    const generation = ctx.feed.generation;
    const actor = ctx.state?.me.id;
    const lane = ctx.feedScope;
    const contentScope = ctx.contentScope ?? 'ordinary';
    const selectedMode = ctx.mode;
    const selectedExperience = ctx.experience;
    document.querySelector('#pull-refresh')?.classList.add('refreshing');
    try {
      await flushSeen();
      const nextState = await ctx.api('/api/state');
      const fresh = new ctx.FeedSession((mode, cursor) =>
        ctx.api(
          `/api/feed?mode=${mode}&scope=${lane}&format=${selectedExperience}&contentScope=${contentScope}${cursor ? '&cursor=' + encodeURIComponent(cursor) : ''}`,
        ),
      );
      fresh.reset(selectedMode);
      await fresh.next();
      if (
        generation !== ctx.feed.generation ||
        ctx.state?.me.id !== actor ||
        ctx.contentScope !== contentScope
      )
        return;
      validateDiscoveryResponse(fresh.meta, contentScope, ctx.discoveryPolicyVersion);
      ctx.state = nextState;
      ctx.feed.reset(ctx.mode);
      ctx.feed.items = fresh.items;
      ctx.feed.cursor = fresh.cursor;
      ctx.feed.started = true;
      ctx.feed.meta = fresh.meta;
      ctx.feedScroll = 0;
      ctx.render();
      window.scrollTo({ top: 0, behavior: 'instant' });
      ctx.toast(
        ctx.contentScope === 'learning' ? 'Learning is up to date.' : 'Your wall is up to date.',
      );
    } catch (error) {
      ctx.toast(error.message);
    } finally {
      ctx.refreshPending = false;
      document.querySelector('#pull-refresh')?.classList.remove('refreshing');
    }
  }
  // Native overflow areas own their gestures; feed gestures begin only on content.
  function isGestureControl(target) {
    for (let node = target; node && node !== ctx.view; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (
        (node.scrollWidth > node.clientWidth && /auto|scroll/.test(style.overflowX)) ||
        (node.scrollHeight > node.clientHeight && /auto|scroll/.test(style.overflowY))
      )
        return true;
    }
    return false;
  }

  let pullStart = null;
  let pull = 0;
  ctx.view.addEventListener(
    'touchstart',
    (event) => {
      if (
        ctx.state &&
        ['feed', 'learning'].includes(ctx.route()[0]) &&
        window.scrollY <= 0 &&
        !ctx.modal.open &&
        !ctx.refreshPending &&
        event.touches.length === 1 &&
        !isGestureControl(event.target) &&
        !event.target.closest(
          'button,a,input,textarea,select,video,audio,summary,label,.stories,.feeling-canvas,.media-items,.mode-wheel,.mode-wheel-host,.media-editor,.conversation',
        )
      ) {
        pullStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      } else pullStart = null;
    },
    { passive: true },
  );
  ctx.view.addEventListener(
    'touchmove',
    (event) => {
      if (event.touches.length !== 1) return;
      pull = ctx.pullDistance(
        pullStart,
        { x: event.touches[0].clientX, y: event.touches[0].clientY },
        window.scrollY <= 0,
      );
      const indicator = document.querySelector('#pull-refresh');
      if (indicator) {
        indicator.hidden = pull < 15;
        indicator.textContent = pull >= 90 ? 'Release to refresh' : 'Pull to refresh';
      }
    },
    { passive: true },
  );
  ctx.view.addEventListener(
    'touchend',
    () => {
      const shouldRefresh = pull >= 90;
      pullStart = null;
      pull = 0;
      const indicator = document.querySelector('#pull-refresh');
      if (indicator) indicator.hidden = true;
      if (shouldRefresh) refreshFeed();
    },
    { passive: true },
  );
  ctx.view.addEventListener(
    'touchcancel',
    () => {
      pullStart = null;
      pull = 0;
      const indicator = document.querySelector('#pull-refresh');
      if (indicator) indicator.hidden = true;
    },
    { passive: true },
  );

  return {
    clearExposure,
    saveFeedPosition,
    flushSeen,
    observeReading,
    refreshFeed,
    isGestureControl,
  };
}
